import { useEffect, useState, useMemo } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { apiFetch } from "../../utils/api";
import { SocialDataSkeleton } from "./SocialSkeleton";
import { AlertTriangle, TrendingDown, TrendingUp, RefreshCw, Search, ChevronDown, ChevronUp } from "lucide-react";
import { formatNumber } from "./common";

const KPI_LABELS = {
  followers: "Followers",
  views:     "Views",
  cpr:       "Costo/Risultato",
  cpa:       "CPA",
  spend:     "Spesa Ads",
  revenue:   "Fatturato",
  roi:       "ROI",
};

const KPI_UNITS = {
  followers: "count",
  views:     "count",
  cpr:       "currency",
  cpa:       "currency",
  spend:     "currency",
  revenue:   "currency",
  roi:       "pct",
};

const KPI_ORDER = ["roi", "cpa", "cpr", "revenue", "spend", "views", "followers"];

const BAND_STYLES = {
  red:    { bg: "bg-red-600",     text: "text-white",   border: "border-l-red-500",     ring: "ring-red-400",     softBg: "bg-red-50",     softText: "text-red-700",     softBorder: "border-red-200",     label: "Critico" },
  yellow: { bg: "bg-amber-500",   text: "text-white",   border: "border-l-amber-500",   ring: "ring-amber-300",   softBg: "bg-amber-50",   softText: "text-amber-700",   softBorder: "border-amber-200",   label: "Da monitorare" },
  green:  { bg: "bg-emerald-600", text: "text-white",   border: "border-l-emerald-500", ring: "ring-emerald-300", softBg: "bg-emerald-50", softText: "text-emerald-700", softBorder: "border-emerald-200", label: "In salute" },
  gray:   { bg: "bg-slate-400",   text: "text-white",   border: "border-l-slate-400",   ring: "ring-slate-200",   softBg: "bg-slate-50",   softText: "text-slate-600",   softBorder: "border-slate-200",   label: "Senza dati" },
};

function fmt(value, unit) {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  if (unit === "currency") return `€${formatNumber(Math.round(value))}`;
  if (unit === "pct")      return `${value.toFixed(1)}%`;
  return formatNumber(Math.round(value));
}

function DeltaChip({ value, inverted }) {
  if (value === null || value === undefined || !isFinite(value)) {
    return <span className="text-[10px] text-slate-400">n/d</span>;
  }
  const positive = value >= 0;
  const good = inverted ? !positive : positive;
  const cls = good ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-red-600 bg-red-50 border-red-200";
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1 py-px rounded border ${cls}`}>
      <Icon className="w-2.5 h-2.5" />
      {positive ? "+" : ""}{value.toFixed(1)}%
    </span>
  );
}

function KpiCell({ name, kpi }) {
  const unit = KPI_UNITS[name];
  const bandStyle = BAND_STYLES[kpi.band_vs_avg || "gray"];
  return (
    <div className={`rounded-lg border p-2 ${bandStyle.softBg} ${bandStyle.softBorder}`} data-testid={`kpi-${name}`}>
      <div className="flex items-center justify-between gap-1 mb-0.5">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 truncate">{KPI_LABELS[name]}</div>
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${BAND_STYLES[kpi.band_vs_avg || "gray"].bg}`} />
      </div>
      <div className="font-bold font-mono text-slate-900 text-[15px] leading-tight">{fmt(kpi.curr, unit)}</div>
      <div className="mt-0.5"><DeltaChip value={kpi.delta_vs_avg} inverted={kpi.inverted} /></div>
      <div className="text-[9px] text-slate-500 mt-1 tabular-nums">avg 4w: {fmt(kpi.avg_4w, unit)}</div>
    </div>
  );
}

function ClientCard({ client, expanded, onToggle }) {
  const band = BAND_STYLES[client.band || "gray"];
  const score = client.score;
  return (
    <Card className={`mb-3 overflow-hidden border border-slate-200 border-l-[5px] bg-gradient-to-r from-white to-slate-50/40 shadow-[0_8px_24px_-12px_rgba(15,23,42,0.28)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-12px_rgba(15,23,42,0.32)] ${band.border}`} data-testid={`health-card-${client.client_id}`}>
      <div className="px-5 py-3">
        <div className="flex items-start gap-4">
          {/* Score badge */}
          <div className={`shrink-0 w-16 h-16 rounded-xl ${band.bg} ${band.text} flex flex-col items-center justify-center shadow-lg ring-4 ring-white`}>
            <div className="text-[10px] font-semibold uppercase tracking-wider opacity-90">Salute</div>
            <div className="text-2xl font-black leading-none">{score !== null && score !== undefined ? score : "—"}</div>
          </div>

          {/* Nome + Alerts */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-base font-bold truncate">{client.name}</h3>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${band.softBg} ${band.softText} ${band.softBorder}`}>
                {band.label}
              </span>
            </div>
            {client.alerts && client.alerts.length > 0 ? (
              <div className="mt-2">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 mb-1.5">
                  <AlertTriangle className="w-3 h-3 text-amber-600" /> Alert
                </div>
                <div className="flex flex-wrap gap-2">
                  {client.alerts.map(a => {
                    const bs = BAND_STYLES[a.level];
                    const arrow = a.direction === "up" ? "↑" : "↓";
                    return (
                      <span key={a.kpi} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${bs.softBg} ${bs.softText} ${bs.softBorder}`}
                            data-testid={`alert-${client.client_id}-${a.kpi}`}>
                        {KPI_LABELS[a.kpi] || a.kpi} {arrow} {Math.abs(a.delta_pct).toFixed(1)}%
                      </span>
                    );
                  })}
                </div>
              </div>
            ) : score !== null && score !== undefined ? (
              <div className="mt-2 text-[11px] text-emerald-700 flex items-center gap-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" /> Nessun alert significativo
              </div>
            ) : null}
          </div>

          <button onClick={onToggle} className="shrink-0 text-xs text-slate-500 hover:text-indigo-700 hover:bg-indigo-50 border border-transparent hover:border-indigo-100 rounded-lg px-3 py-2 flex items-center gap-1.5 transition-all" data-testid={`toggle-${client.client_id}`}>
            {expanded ? <>Nascondi KPI <ChevronUp className="w-3 h-3" /></> : <>Dettagli KPI <ChevronDown className="w-3 h-3" /></>}
          </button>
        </div>

        {/* KPI grid */}
        {expanded && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid={`kpis-${client.client_id}`}>
            {KPI_ORDER.map(name => <KpiCell key={name} name={name} kpi={client.kpis[name]} />)}
          </div>
        )}

        {/* Trend WoW e Live (visibile solo se expanded) */}
        {expanded && (
          <div className="mt-3 border-t pt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]" data-testid={`trends-${client.client_id}`}>
            <div>
              <div className="font-semibold text-slate-600 mb-1">Trend Settimanale (WoW)</div>
              <div className="flex flex-wrap gap-1.5">
                {KPI_ORDER.filter(k => client.kpis[k].delta_wow !== null && client.kpis[k].delta_wow !== undefined).map(k => (
                  <span key={k} className="inline-flex items-center gap-1 text-[10px] text-slate-700">
                    {KPI_LABELS[k]}: <DeltaChip value={client.kpis[k].delta_wow} inverted={client.kpis[k].inverted} />
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="font-semibold text-slate-600 mb-1">Trend Live (stessi giorni)</div>
              <div className="flex flex-wrap gap-1.5">
                {KPI_ORDER.filter(k => client.kpis[k].delta_live !== null && client.kpis[k].delta_live !== undefined).map(k => (
                  <span key={k} className="inline-flex items-center gap-1 text-[10px] text-slate-700">
                    {KPI_LABELS[k]}: <DeltaChip value={client.kpis[k].delta_live} inverted={client.kpis[k].inverted} />
                  </span>
                ))}
                {KPI_ORDER.every(k => client.kpis[k].delta_live === null) && (
                  <span className="text-slate-400">Nessun dato live disponibile</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

export function HealthTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const [search, setSearch] = useState("");
  const [flagFilter, setFlagFilter] = useState("all");
  const [expanded, setExpanded] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await apiFetch("/clients-health/overview");
      if (r.ok) setData(await r.json());
      else toast.error("Errore caricamento overview");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const runSnapshotNow = async () => {
    if (!window.confirm("Forzare snapshot settimanale? (il cron va comunque ogni lunedì 06:00)")) return;
    setSnapshotting(true);
    try {
      const r = await apiFetch("/clients-health/snapshot-now", { method: "POST" });
      if (r.ok) { toast.success("Snapshot completato"); fetchData(); }
      else toast.error("Errore snapshot");
    } finally { setSnapshotting(false); }
  };

  const filtered = useMemo(() => {
    const list = data?.clients || [];
    return list.filter(c => {
      if (search && !(c.name || "").toLowerCase().includes(search.toLowerCase())) return false;
      if (flagFilter !== "all" && c.band !== flagFilter) return false;
      return true;
    });
  }, [data, search, flagFilter]);

  const counts = useMemo(() => {
    const acc = { red: 0, yellow: 0, green: 0, gray: 0 };
    for (const c of data?.clients || []) acc[c.band || "gray"]++;
    return acc;
  }, [data]);

  const toggleAll = (open) => {
    const next = {};
    for (const c of data?.clients || []) next[c.client_id] = open;
    setExpanded(next);
  };

  return (
    <div className="space-y-6">
      <Card className="p-5 border-slate-200 bg-white/90 shadow-[0_10px_30px_-16px_rgba(15,23,42,0.3)]">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-red-500" />
          <span className="font-semibold">Salute Clienti</span>
          {data?.windows && (
            <span className="text-[10px] text-slate-500">
              · settimana {data.windows.last?.from} → {data.windows.last?.to} · media 4w {data.windows.avg_4w?.from} → {data.windows.avg_4w?.to}
            </span>
          )}
        </div>
        <div className="ml-auto flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca cliente..." className="pl-8 h-9 w-56 text-xs bg-slate-50 shadow-sm focus:bg-white" data-testid="health-search" />
          </div>
          <Button variant="outline" size="sm" onClick={() => toggleAll(true)} data-testid="health-expand-all">Espandi tutto</Button>
          <Button variant="outline" size="sm" onClick={() => toggleAll(false)} data-testid="health-collapse-all">Comprimi</Button>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="health-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
          <Button variant="outline" size="sm" onClick={runSnapshotNow} disabled={snapshotting} data-testid="health-snapshot-now">
            {snapshotting ? "Snapshot..." : "Snapshot ora"}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap mt-5 pt-5 border-t border-slate-100">
        {[
          { v: "all",    l: `Tutti (${data?.clients?.length || 0})`, color: "bg-slate-900 text-white" },
          { v: "red",    l: `Critici (${counts.red})`,               color: "bg-red-600 text-white" },
          { v: "yellow", l: `Da monitorare (${counts.yellow})`,      color: "bg-amber-500 text-white" },
          { v: "green",  l: `In salute (${counts.green})`,           color: "bg-emerald-600 text-white" },
          { v: "gray",   l: `Senza dati (${counts.gray})`,           color: "bg-slate-500 text-white" },
        ].map(opt => (
          <button key={opt.v} onClick={() => setFlagFilter(opt.v)} data-testid={`health-filter-${opt.v}`}
            className={`px-4 py-2 text-xs font-medium rounded-full border transition-all duration-200 ${flagFilter === opt.v ? `${opt.color} border-transparent shadow-md` : "bg-white text-slate-600 border-slate-200 shadow-sm hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-md"}`}>
            {opt.l}
          </button>
        ))}
        <div className="text-[10px] text-slate-500 ml-auto">
          Score: 🔴 0-59 · 🟡 60-84 · 🟢 85-100 · Peggiori in cima
        </div>
      </div>
      </Card>

      {loading && <div className="mt-4"><SocialDataSkeleton variant="list" rows={5} /></div>}
      {!loading && filtered.length === 0 && (
        <Card className="mt-4 p-8 text-center text-sm text-muted-foreground">
          {(!data?.clients || data.clients.length === 0)
            ? "Nessun cliente attivo con Zernio collegato."
            : "Nessun cliente corrisponde ai filtri."}
        </Card>
      )}

      {!loading && filtered.length > 0 && (
        <div className="mt-4" data-testid="health-list">
          {filtered.map(c => (
            <ClientCard
              key={c.client_id}
              client={c}
              expanded={!!expanded[c.client_id]}
              onToggle={() => setExpanded(prev => ({ ...prev, [c.client_id]: !prev[c.client_id] }))}
            />
          ))}
        </div>
      )}
    </div>
  );
}
